import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import { Trophy, Sparkles, Share2 } from "lucide-react";

export default function ReferralLeaderboard() {
  const [data, setData] = useState(null);
  const [limit, setLimit] = useState(25);
  const [err, setErr] = useState("");

  useEffect(() => {
    setData(null); setErr("");
    api.get(`/referrals/leaderboard?limit=${limit}`)
      .then(r => setData(r.data))
      .catch(e => setErr(e.response?.data?.detail || "Could not load leaderboard"));
  }, [limit]);

  return (
    <div className="max-w-5xl mx-auto px-6 py-16" data-testid="referral-leaderboard-page">
      <Seo title="Referral leaderboard · The Farm To You" path="/referrals/leaderboard" />

      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
        <div>
          <p className="overline">Member spotlight</p>
          <h1 className="font-display text-4xl sm:text-5xl mt-3 flex items-center gap-3">
            <Trophy className="text-amber-500" size={42} aria-hidden/>
            Top referrers
          </h1>
          <p className="text-[var(--ff-text-secondary)] mt-3 max-w-xl leading-relaxed">
            Members who've brought the most new families to the table. Earn{" "}
            <strong>${data?.reward_per_conversion ?? 10} credit</strong> for both you and your friend on every signup.
          </p>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <span className="text-xs text-[var(--ff-text-muted)]">Show</span>
          {[10, 25, 50].map(n => (
            <button
              key={n}
              onClick={() => setLimit(n)}
              className={`px-3 py-1.5 rounded-full text-xs ${limit === n ? "bg-[var(--ff-primary)] text-white" : "border border-[var(--ff-border)]"}`}
              data-testid={`leaderboard-limit-${n}`}
            >Top {n}</button>
          ))}
        </div>
      </div>

      {err && <p className="text-rose-700" data-testid="leaderboard-error">{err}</p>}
      {!data && !err && <p className="text-[var(--ff-text-muted)]" data-testid="leaderboard-loading">Loading leaderboard…</p>}

      {data && (
        <>
          {data.rows.length === 0 ? (
            <div className="card-flat p-12 text-center" data-testid="leaderboard-empty">
              <Sparkles className="mx-auto text-[var(--ff-text-muted)]" size={32}/>
              <h3 className="font-display text-2xl mt-3">Be the first.</h3>
              <p className="text-sm text-[var(--ff-text-muted)] mt-2 max-w-md mx-auto">
                Nobody's on the board yet. Share your referral link from your account and snag the #1 spot.
              </p>
              <Link to="/account" className="btn-primary mt-6 inline-flex items-center gap-2" data-testid="leaderboard-cta-empty">
                <Share2 size={16}/> Get my referral link
              </Link>
            </div>
          ) : (
            <div className="card-flat overflow-hidden" data-testid="leaderboard-table">
              <div className="grid grid-cols-12 text-xs uppercase tracking-wider text-[var(--ff-text-muted)] px-5 py-3 border-b border-[var(--ff-border)] bg-[var(--ff-muted)]">
                <div className="col-span-1">Rank</div>
                <div className="col-span-6 md:col-span-7">Member</div>
                <div className="col-span-3 md:col-span-2 text-right">Invites</div>
                <div className="col-span-2 text-right">Converted</div>
              </div>
              <ul>
                {data.rows.map((r) => {
                  const medal = r.rank === 1 ? "🥇" : r.rank === 2 ? "🥈" : r.rank === 3 ? "🥉" : null;
                  return (
                    <li
                      key={r.rank}
                      className={`grid grid-cols-12 items-center px-5 py-4 border-b border-[var(--ff-border)] last:border-0 ${r.rank <= 3 ? "bg-amber-50/40" : ""}`}
                      data-testid={`leaderboard-row-${r.rank}`}
                    >
                      <div className="col-span-1 font-display text-2xl">{medal || <span className="text-[var(--ff-text-muted)] text-base">#{r.rank}</span>}</div>
                      <div className="col-span-6 md:col-span-7">
                        <p className="font-medium">{r.display_name}</p>
                        {r.is_founding_member && (
                          <span className="inline-block mt-1 text-[10px] uppercase tracking-wider bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">
                            Founding Member
                          </span>
                        )}
                      </div>
                      <div className="col-span-3 md:col-span-2 text-right text-[var(--ff-text-secondary)]" data-testid={`leaderboard-total-${r.rank}`}>{r.total}</div>
                      <div className="col-span-2 text-right font-display text-lg" data-testid={`leaderboard-converted-${r.rank}`}>{r.converted}</div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          <div className="mt-10 card-flat p-6 flex flex-col md:flex-row gap-4 md:items-center justify-between" data-testid="leaderboard-cta">
            <div>
              <p className="overline">Climb the board</p>
              <h3 className="font-display text-2xl mt-1">Bring your people. Stack credits.</h3>
              <p className="text-sm text-[var(--ff-text-secondary)] mt-1">
                Every friend who joins earns you both ${data.reward_per_conversion} toward your next renewal.
              </p>
            </div>
            <Link to="/account" className="btn-primary inline-flex items-center gap-2" data-testid="leaderboard-cta-account">
              <Share2 size={16}/> Get my link
            </Link>
          </div>
        </>
      )}
    </div>
  );
}
